import { match } from '@formatjs/intl-localematcher';

export interface ResolveInitialLocaleOptions {
  /** previously persisted locale (localStorage['locale']), if any */
  stored: string | null;
  /** browser preference list (navigator.languages) */
  browserLanguages: readonly string[];
  /** language keys supported by this deployment (config i18n.languages) */
  supported: string[];
  /** fallback language (config i18n.defaultLanguage) */
  defaultLanguage: string;
}

/**
 * Resolves the locale to bootstrap the app with, matched against the
 * deployment's supported languages.
 *
 * Without this, the first boot persists the raw region-tagged
 * `navigator.language` (e.g. `de-DE`) as LOCALE_ID while the supported
 * languages use base keys (e.g. `en`) — a guaranteed mismatch that forced
 * `appLocalGuard` into a mid-navigation `location.reload()` on the first
 * loaded archive, losing the in-memory container. A stored-but-unsupported
 * locale from an older session self-heals to a supported one here.
 */
export function resolveInitialLocale(
  options: ResolveInitialLocaleOptions,
): string {
  const { stored, browserLanguages, supported, defaultLanguage } = options;

  const candidates = [...(stored ? [stored] : []), ...browserLanguages].filter(
    (candidate) => {
      if (typeof candidate !== 'string' || !candidate.length) {
        return false;
      }
      try {
        // drop invalid BCP 47 tags (e.g. garbage in localStorage) individually
        Intl.getCanonicalLocales(candidate);
        return true;
      } catch {
        return false;
      }
    },
  );

  if (!candidates.length || !supported.length) {
    return defaultLanguage;
  }

  try {
    return match(candidates, supported, defaultLanguage);
  } catch {
    return defaultLanguage;
  }
}
